import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { toasterState } from "Components/common/toaster";
import AgentsCrumb from "./AgentsCrumb";
import {
  TEMPLATES as ALL_TEMPLATES, STARTS, START_FILTERS as ALL_START_FILTERS, TEAMS, WORKS_WITH, filterTemplates, usesSignals
} from "./templateCatalog";
import { signalsOn } from "Pages/Dashboard/signals/signalsGate";
import { defaultTimezone } from "./DayOfWeekField";
import "./agentTemplates.scss";

/** The agents intro; it names Signals only when the team has them. */
export const agentsIntro = () =>
  `Agents do the work for you, step by step. Each one starts ${signalsOn() ? "from a signal, " : ""}from something that happens in Salesgear, on a schedule, or when you run it.`;

export const planLine = usage => {
  const base = "Agents run free; finding an email costs 1 credit, a phone 10, research 10";
  return usage && usage.eligible > 0 ? `${base} · ${usage.used || 0} of ${usage.eligible} agents on your plan` : base;
};

const AVAIL = [{ value: "available", label: "Available now" }, { value: "soon", label: "Coming soon" }];
const SORTS = [{ value: "recommended", label: "Recommended" }, { value: "az", label: "A to Z" }];
const NOTIFY_KEY = "agents.templates.notify";
const readNotify = () => { try { return JSON.parse(localStorage.getItem(NOTIFY_KEY) || "[]"); } catch (e) { return []; } };

/** A filter button with a small checklist menu. Applied = neutral dark outline, never blue. */
const FilterMenu = ({ label, options, picked, onChange, single = false, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const applied = !single && picked.length > 0;
  const toggle = v => {
    if (single) { onChange(v); setOpen(false); return; }
    onChange(picked.includes(v) ? picked.filter(x => x !== v) : [...picked, v]);
  };
  const current = single ? (options.find(o => o.value === picked) || {}).label : "";
  return (
    <div className="agt-fmenu" ref={ref}>
      <button type="button" data-testid={testId} aria-expanded={open}
        className={`agt-fbtn${applied || single ? " is-applied" : ""}`} onClick={() => setOpen(o => !o)}>
        {single ? <>{label}: <b>{current}</b></> : label}
        {applied && <span className="agt-fbtn__n">{picked.length}</span>}
        <i className="material-icons-outlined">expand_more</i>
      </button>
      {open && (
        <div className="agt-fmenu__list" role="menu">
          {options.map(o => {
            const on = single ? picked === o.value : picked.includes(o.value);
            return (
              <button type="button" role="menuitemcheckbox" aria-checked={on} key={o.value}
                className="agt-fmenu__opt" onClick={() => toggle(o.value)}>
                <i className="material-icons-outlined">{on ? (single ? "radio_button_checked" : "check_box") : (single ? "radio_button_unchecked" : "check_box_outline_blank")}</i>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** The trigger chip, then neutral step chips with chevrons between. */
export const StepRow = ({ t }) => (
  <div className="agt-steps">
    {t.trigger && (
      <span className={`agt-trig agt-trig--${t.trigger.kind === "signal" ? t.trigger.family : "event"}`}>
        <i className="material-icons-outlined">{t.trigger.kind === "signal" ? "wifi_tethering" : "flash_on"}</i>{t.trigger.label}
      </span>
    )}
    {t.steps.map((s, i) => (
      <React.Fragment key={s.label}>
        {(i > 0 || t.trigger) && <i className="material-icons-outlined agt-steps__chev">chevron_right</i>}
        <span className="agt-step"><i className="material-icons-outlined">{s.icon}</i>{s.label}</span>
      </React.Fragment>
    ))}
  </div>
);

// Portalled to the body so the backdrop covers the right rail too (.mainContent is a containing block).
const HowItWorks = ({ t, onClose, onUse }) => {
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return ReactDOM.createPortal(
  <div className="agt-how__overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="agt-how" role="dialog" aria-label={`How ${t.name} works`}>
      <div className="agt-how__head">
        <span className={`agt-tile agt-tile--${t.trigger && t.trigger.kind === "signal" ? t.trigger.family : "plain"}`}>
          <i className="material-icons-outlined">{t.icon}</i>
        </span>
        <div className="agt-how__titles">
          <div className="agt-how__name">{t.name}</div>
          <div className="agt-card__starts">{STARTS[t.starts]}</div>
        </div>
        <button type="button" className="agt-how__close" aria-label="Close" onClick={onClose}>
          <i className="material-icons-outlined">close</i>
        </button>
      </div>
      <p className="agt-how__does">{t.does}</p>
      <ol className="agt-how__list">
        <li><b>Starts</b><span>{t.trigger ? t.trigger.label : STARTS[t.starts]}</span></li>
        {t.steps.map(s => <li key={s.label}><b>Then</b><span>{s.label}</span></li>)}
      </ol>
      <p className="agt-how__note">You review it before it goes live. Nothing is sent until you turn it on.</p>
      <div className="agt-how__actions">
        {t.soon ? <span className="agt-soon">Coming soon</span> : (
          <button type="button" className="ag-btn" onClick={() => onUse(t)}>Use this</button>
        )}
      </div>
    </div>
  </div>,
  document.body
  );
};

const TemplateCard = ({ t, onUse, onHow, notified, onNotify }) => (
  <div className={`agt-card${t.soon ? " is-soon" : ""}`} data-testid="agt-card">
    <div className="agt-card__top">
      <span className={`agt-tile agt-tile--${t.trigger && t.trigger.kind === "signal" ? t.trigger.family : "plain"}`}>
        <i className="material-icons-outlined">{t.icon}</i>
      </span>
      <div className="agt-card__titles">
        <div className="agt-card__namerow">
          <span className="agt-card__name">{t.name}</span>
          {t.soon && <span className="agt-soon">Coming soon</span>}
        </div>
        <div className="agt-card__starts">{STARTS[t.starts]}</div>
      </div>
    </div>
    <div className="agt-card__does">{t.does}</div>
    <StepRow t={t} />
    <div className="agt-card__actions">
      {t.soon ? (
        <button type="button" className="ag-btn ag-btn--sm" disabled={notified} onClick={() => onNotify(t)}>
          <i className="material-icons-outlined">notifications</i>{notified ? "We'll tell you" : "Notify me"}
        </button>
      ) : (
        <button type="button" className="ag-btn ag-btn--sm" onClick={() => onUse(t)}>Use this</button>
      )}
      <button type="button" className="agt-how-btn" onClick={() => onHow(t)}>
        <i className="material-icons-outlined">info</i>How it works
      </button>
    </div>
  </div>
);

/**
 * Team chips, search, filters and the template cards, grouped by team. onUse(template) opens a ready one; a template
 * the server composes (`compose`) opens straight in the builder with its schedule filled in.
 */
export const TemplatesBrowser = observer(({ onUse }) => {
  const history = useHistory();
  const [team, setTeam] = useState("");
  const [q, setQ] = useState("");
  const [starts, setStarts] = useState([]);
  const [works, setWorks] = useState([]);
  const [avail, setAvail] = useState([]);
  const [sort, setSort] = useState("recommended");
  const [how, setHow] = useState(null);
  const [notified, setNotified] = useState(readNotify);

  useEffect(() => { if (!agentsData.usage) agentsData.fetchUsage(); }, []);

  const use = async t => {
    if (!t.compose) return onUse(t);
    if (!(await agentsData.canHireAgent())) return null;
    const draft = await agentsData.composeTemplate(t.compose.key, { ...t.compose.payload, timezone: defaultTimezone() });
    if (!draft) return toasterState.setToastMsg(`Couldn't open ${t.name} right now. Try again.`, "failure");
    agentsData.stageDraft(draft);
    return history.push("/agents/new/custom");
  };

  // Signal agents are offered only when Signals are on for the team.
  const TEMPLATES = ALL_TEMPLATES.filter(t => signalsOn() || !usesSignals(t));
  const START_FILTERS = ALL_START_FILTERS.filter(f => signalsOn() || f.value !== "signal");
  const shown = filterTemplates(TEMPLATES, { team, q, starts, works, avail, sort });
  // Chip counts follow the search and filters, not the team itself (same as the Signals Library).
  const matching = filterTemplates(TEMPLATES, { q, starts, works, avail, sort });
  const labelOf = (opts, v) => (opts.find(o => o.value === v) || {}).label || v;
  const tags = [
    ...starts.map(v => ({ key: `s-${v}`, label: labelOf(START_FILTERS, v), remove: () => setStarts(starts.filter(x => x !== v)) })),
    ...works.map(v => ({ key: `w-${v}`, label: v, remove: () => setWorks(works.filter(x => x !== v)) })),
    ...avail.map(v => ({ key: `a-${v}`, label: labelOf(AVAIL, v), remove: () => setAvail(avail.filter(x => x !== v)) }))
  ];
  const narrowed = tags.length > 0 || !!q.trim() || !!team;
  const clearAll = () => { setStarts([]); setWorks([]); setAvail([]); setQ(""); setTeam(""); };

  // ponytail: "Notify me" is remembered in this browser only; wire to a server list when one exists.
  const notify = t => {
    const next = Array.from(new Set([...notified, t.key]));
    setNotified(next);
    try { localStorage.setItem(NOTIFY_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
    toasterState.setToastMsg(`Got it. We'll let you know when ${t.name} is ready.`, "success");
  };

  const sections = sort === "az" || team
    ? [{ title: "", items: shown }]
    : TEAMS.map(c => ({ title: c, items: shown.filter(t => t.team === c) })).filter(s => s.items.length);

  return (
    <div className="agt">
      <div className="agt-bar">
        <div className="agt-cats" role="tablist" aria-label="Teams">
          {[{ v: "", label: `All · ${matching.length}` },
            ...TEAMS.filter(c => TEMPLATES.some(t => t.team === c)).map(c => ({ v: c, label: `${c} · ${matching.filter(t => t.team === c).length}` }))].map(c => (
            <button key={c.v || "all"} type="button" role="tab" aria-selected={team === c.v}
              className={`agt-cat${team === c.v ? " is-on" : ""}`} onClick={() => setTeam(c.v)}>{c.label}</button>
          ))}
        </div>
        {/* Each clause wraps as a whole, so "10 of 50 agents on your plan" never breaks mid-phrase at the edge. */}
        <span className="agt-plan">
          {planLine(agentsData.usage).split(" · ").map((part, i) => (
            <React.Fragment key={part}>{i > 0 && " · "}<span className="agt-plan__part">{part}</span></React.Fragment>
          ))}
        </span>
      </div>

      <div className="agt-bar">
        <label className="agt-search">
          <i className="material-icons-outlined">search</i>
          <input aria-label="Search templates" placeholder="Search templates, e.g. reply, list, job change"
            value={q} onChange={e => setQ(e.target.value)} />
        </label>
        <FilterMenu label="Starts" testId="f-starts" options={START_FILTERS} picked={starts} onChange={setStarts} />
        <FilterMenu label="Works with" testId="f-works" options={WORKS_WITH.map(w => ({ value: w, label: w }))} picked={works} onChange={setWorks} />
        <FilterMenu label="Availability" testId="f-avail" options={AVAIL} picked={avail} onChange={setAvail} />
        <span className="agt-bar__gap" />
        <FilterMenu label="Sort" testId="f-sort" single options={SORTS} picked={sort} onChange={setSort} />
      </div>

      <div className="agt-count">
        <span>Showing <b>{shown.length}</b> of {TEMPLATES.length} agents</span>
        {tags.map(t => (
          <span className="agt-tag" key={t.key}>
            {t.label}
            <button type="button" aria-label={`Remove ${t.label}`} onClick={t.remove}><i className="material-icons-outlined">close</i></button>
          </span>
        ))}
        {narrowed && <button type="button" className="agt-clear" onClick={clearAll}>Clear all</button>}
      </div>

      {shown.length === 0 ? (
        <div className="ag-empty ag-empty--quiet">
          <div className="ag-empty__title">No templates match</div>
          <div className="ag-empty__actions"><button type="button" className="ag-btn" onClick={clearAll}>Clear all</button></div>
        </div>
      ) : sections.map(s => (
        <section key={s.title || "all"} className="agt-section">
          {s.title && <h2 className="agt-section__title">{s.title}<span>{s.items.length}</span></h2>}
          <div className="agt-grid">
            {s.items.map(t => (
              <TemplateCard key={t.key} t={t} onUse={use} onHow={setHow} notified={notified.includes(t.key)} onNotify={notify} />
            ))}
          </div>
        </section>
      ))}

      {how && <HowItWorks t={how} onClose={() => setHow(null)} onUse={x => { setHow(null); use(x); }} />}
    </div>
  );
});

/** /agents/templates: the Templates page on its own. */
const AgentTemplatesPage = () => {
  const history = useHistory();
  const use = async t => { if (t.to && (await agentsData.canHireAgent())) history.push(t.to); };
  return (
    <div className="ag-shell">
      <div className="ag-shell__head">
        <AgentsCrumb items={[{ label: "Templates" }]} />
      </div>
      <div className="ag-shell__body agt-shell__body">
        <div className="ag-shell__main">
          <div className="ag-listhead">
            <h1 className="ag-listhead__title">Templates</h1>
            <p className="ag-listhead__sub">{agentsIntro()}</p>
          </div>
          <TemplatesBrowser onUse={use} />
        </div>
      </div>
    </div>
  );
};

export default AgentTemplatesPage;
